import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supportApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  LifeBuoy, Plus, MessageSquare, Clock, CheckCircle, 
  AlertCircle, Loader2, Send, ChevronRight, User, Headphones
} from 'lucide-react';

const CATEGORIES = [
  { value: 'booking_issue', label: 'Booking Issue' },
  { value: 'payment', label: 'Payment Problem' },
  { value: 'dispute', label: 'Dispute with User' },
  { value: 'account', label: 'Account Help' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'other', label: 'Other' },
];

const STATUS_BADGES = {
  open: { label: 'Open', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  waiting_user: { label: 'Waiting for You', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  resolved: { label: 'Resolved', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  closed: { label: 'Closed', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const Support = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: '',
    description: '',
    priority: 'normal',
    booking_id: ''
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await supportApi.getMyTickets();
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicket.subject || !newTicket.category || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await supportApi.createTicket(newTicket);
      toast.success('Support ticket created');
      setTickets([response.data.ticket, ...tickets]);
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', category: '', description: '', priority: 'normal', booking_id: '' });
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      await supportApi.replyToTicket(viewTicket.ticket_id, replyText);
      toast.success('Reply sent');
      setReplyText('');
      // Refresh ticket
      const response = await supportApi.getTicket(viewTicket.ticket_id);
      setViewTicket(response.data);
      loadTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      await supportApi.closeTicket(viewTicket.ticket_id);
      toast.success('Ticket closed');
      setViewTicket(null);
      loadTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Support Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Get help with your account, bookings, or payments
            </p>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="rounded-xl"
            data-testid="create-ticket-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* Quick Help Cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Card className="border-border/50 hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="font-medium mb-1">FAQs</h3>
              <p className="text-sm text-muted-foreground">Common questions</p>
            </CardContent>
          </Card>
          <Card className="border-border/50 hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <LifeBuoy className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="font-medium mb-1">Help Center</h3>
              <p className="text-sm text-muted-foreground">Guides & tutorials</p>
            </CardContent>
          </Card>
        </div>

        {/* Tickets List */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5" />
              Your Support Tickets
            </CardTitle>
            <CardDescription>
              {tickets.length === 0 
                ? "No tickets yet. Create one if you need help!"
                : `${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <div className="text-center py-12">
                <LifeBuoy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Support Tickets</h3>
                <p className="text-muted-foreground mb-4">
                  Need help? Create a ticket and we'll get back to you soon.
                </p>
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  className="rounded-xl"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Ticket
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => setViewTicket(ticket)}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors group"
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium truncate">{ticket.subject}</span>
                        <Badge 
                          variant="outline" 
                          className={STATUS_BADGES[ticket.status]?.className}
                        >
                          {STATUS_BADGES[ticket.status]?.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{CATEGORIES.find(c => c.value === ticket.category)?.label}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Ticket Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="rounded-xl"
                  data-testid="ticket-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={newTicket.category}
                  onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="ticket-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Booking ID (optional)</Label>
                <Input
                  value={newTicket.booking_id}
                  onChange={(e) => setNewTicket({ ...newTicket, booking_id: e.target.value })}
                  placeholder="If this is about a specific booking"
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Please describe your issue in detail..."
                  rows={4}
                  className="rounded-xl"
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setCreateDialogOpen(false)}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="rounded-xl"
                  data-testid="submit-ticket-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Ticket Dialog */}
        <Dialog open={!!viewTicket} onOpenChange={() => setViewTicket(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewTicket?.subject}
                <Badge 
                  variant="outline" 
                  className={STATUS_BADGES[viewTicket?.status]?.className}
                >
                  {STATUS_BADGES[viewTicket?.status]?.label}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            {viewTicket && (
              <div className="flex-1 overflow-y-auto space-y-4">
                {/* Ticket Info */}
                <div className="text-sm text-muted-foreground flex items-center gap-3">
                  <span>{CATEGORIES.find(c => c.value === viewTicket.category)?.label}</span>
                  <span>•</span>
                  <span>Created {formatDate(viewTicket.created_at)}</span>
                  {viewTicket.booking_id && (
                    <>
                      <span>•</span>
                      <span>Booking: {viewTicket.booking_id}</span>
                    </>
                  )}
                </div>

                <Separator />

                {/* Messages */}
                <div className="space-y-4 max-h-[300px] overflow-y-auto">
                  {viewTicket.messages?.map((msg, idx) => (
                    <div 
                      key={idx}
                      className={`flex gap-3 ${msg.sender === 'support' ? '' : 'flex-row-reverse'}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        msg.sender === 'support' ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        {msg.sender === 'support' ? (
                          <Headphones className="h-4 w-4 text-primary" />
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </div>
                      <div className={`flex-1 ${msg.sender === 'support' ? '' : 'text-right'}`}>
                        <div className={`inline-block p-3 rounded-xl max-w-[80%] ${
                          msg.sender === 'support' 
                            ? 'bg-primary/10 text-left' 
                            : 'bg-muted text-left'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Input */}
                {viewTicket.status !== 'closed' && (
                  <>
                    <Separator />
                    <div className="flex gap-2">
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        rows={2}
                        className="rounded-xl flex-1"
                        data-testid="ticket-reply-input"
                      />
                      <Button 
                        onClick={handleReply}
                        disabled={!replyText.trim() || submitting}
                        className="rounded-xl self-end"
                        data-testid="send-reply-btn"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex justify-between pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setViewTicket(null)}
                    className="rounded-xl"
                  >
                    Close
                  </Button>
                  {viewTicket.status !== 'closed' && (
                    <Button 
                      variant="secondary"
                      onClick={handleCloseTicket}
                      className="rounded-xl"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark Resolved
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Support;
